import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import IdleWarning from "@/components/IdleWarning";
import Login from "@/pages/Login";
import CallerLink from "@/pages/CallerLink";
import AddonManager from "@/pages/AddonManager";
import CallSurge from "@/pages/CallSurge";
import Wallboard from "@/pages/Wallboard";
import AiAssistant from "@/pages/AiAssistant";
import Dashboard from "@/pages/Dashboard";
import Dispatch from "@/pages/Dispatch";
import NewCall from "@/pages/NewCall";
import IncidentDetail from "@/pages/IncidentDetail";
import CallTypes from "@/pages/CallTypes";
import Premise from "@/pages/Premise";
import RMS from "@/pages/RMS";
import RmsReport from "@/pages/RmsReport";
import RmsReportsList from "@/pages/RmsReportsList";
import DailyActivity from "@/pages/DailyActivity";
import Supervisor from "@/pages/Supervisor";
import MDT from "@/pages/MDT";
import Admin from "@/pages/Admin";
import TrainerConsole from "@/pages/TrainerConsole";
import NCIC from "@/pages/NCIC";
import Help from "@/pages/Help";
import ExclusionNotices from "@/pages/ExclusionNotices";
import Comms from "@/pages/Comms";
import Directory from "@/pages/Directory";
import CallAudit from "@/pages/CallAudit";
import TransferLog from "@/pages/TransferLog";
import CommsDailyReport from "@/pages/CommsDailyReport";
import SearchCalls from "@/pages/SearchCalls";
import ServiceOfProcess from "@/pages/ServiceOfProcess";
import CrimeIntel from "@/pages/CrimeIntel";
import PublicPortal from "@/pages/PublicPortal";
import LocateCaller from "@/pages/LocateCaller";
import IA from "@/pages/IA";
import EventHistory from "@/pages/EventHistory";
import IntegrationCenter from "@/pages/IntegrationCenter";
import Watch from "@/pages/Watch";
import SystemStatus from "@/pages/SystemStatus";
import CommandCenter from "@/pages/CommandCenter";
import SecurityCenter from "@/pages/SecurityCenter";
import QaReview from "@/pages/QaReview";
import Readiness from "@/pages/Readiness";
import RepeatLocations from "@/pages/RepeatLocations";
import BaltimoreCfs from "@/pages/BaltimoreCfs";
import MobileOps from "@/pages/MobileOps";
import Links from "@/pages/Links";
import Fleet from "@/pages/Fleet";
import Policies from "@/pages/Policies";
import ResourceStatus from "@/pages/ResourceStatus";
import ShiftIntel from "@/pages/ShiftIntel";

const CONSTABLE_ALLOWED = ["/mdt", "/ncic", "/incident", "/directory", "/new-call", "/mobile"];

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null)
    return <div className="h-screen w-screen flex items-center justify-center bg-[#090A0C] text-[#9CA3AF] font-mono text-sm">Establishing secure session…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // PA state constables are field-only: MDT + NCIC (+ their own incident). No command-center access.
  if (user.role === "pa_state_constable" && !CONSTABLE_ALLOWED.some((p) => location.pathname.startsWith(p)))
    return <Navigate to="/mdt" replace />;
  if (roles && user.role !== "admin" && !roles.includes(user.role))
    return <div data-testid="route-forbidden" className="h-screen w-screen flex items-center justify-center bg-[#090A0C] text-[#EF4444] font-mono text-sm">403 — Insufficient privileges for this console.</div>;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <IdleWarning />
        <Toaster position="top-center" theme="dark" closeButton duration={5000} toastOptions={{ style: { background: "#1E1F24", border: "1px solid #2C2D35", color: "#F3F4F6", borderRadius: 0, fontFamily: "IBM Plex Mono, monospace", fontSize: 12 } }} />
        <InstallPrompt />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/caller/:token" element={<CallerLink />} />
            <Route path="/addons" element={<Protected roles={["admin"]}><AddonManager /></Protected>} />
            <Route path="/surge" element={<Protected><CallSurge /></Protected>} />
            <Route path="/wallboard" element={<Protected><Wallboard /></Protected>} />
            <Route path="/assistant" element={<Protected><AiAssistant /></Protected>} />
            <Route path="/event-history" element={<Protected><EventHistory /></Protected>} />
            <Route path="/integrations" element={<Protected roles={["supervisor", "watch_commander"]}><IntegrationCenter /></Protected>} />
            <Route path="/watch" element={<Protected><Watch /></Protected>} />
            <Route path="/system-status" element={<Protected><SystemStatus /></Protected>} />
            <Route path="/command" element={<Protected roles={["supervisor", "watch_commander", "police_dispatcher", "fire_dispatcher", "ems_dispatcher", "records_supervisor"]}><CommandCenter /></Protected>} />
            <Route path="/security" element={<Protected roles={["supervisor", "watch_commander"]}><SecurityCenter /></Protected>} />
            <Route path="/qa" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor"]}><QaReview /></Protected>} />
            <Route path="/readiness" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor", "police_dispatcher", "fire_dispatcher", "ems_dispatcher", "dispatcher"]}><Readiness /></Protected>} />
            <Route path="/repeat-locations" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor", "police_dispatcher", "fire_dispatcher", "ems_dispatcher", "dispatcher"]}><RepeatLocations /></Protected>} />
            <Route path="/baltimore" element={<Protected><BaltimoreCfs /></Protected>} />
            <Route path="/mobile" element={<Protected><MobileOps /></Protected>} />
            <Route path="/links" element={<Protected><Links /></Protected>} />
            <Route path="/fleet" element={<Protected><Fleet /></Protected>} />
            <Route path="/policies" element={<Protected><Policies /></Protected>} />
            <Route path="/resources" element={<Protected><ResourceStatus /></Protected>} />
            <Route path="/shift-intel" element={<Protected><ShiftIntel /></Protected>} />
            <Route path="/public/*" element={<PublicPortal />} />
            <Route path="/locate/:token" element={<LocateCaller />} />
            <Route path="/public" element={<PublicPortal />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/dispatch" element={<Protected><Dispatch /></Protected>} />
            <Route path="/new-call" element={<Protected><NewCall /></Protected>} />
            <Route path="/incident/:id" element={<Protected><IncidentDetail /></Protected>} />
            <Route path="/call-types" element={<Protected><CallTypes /></Protected>} />
            <Route path="/search-calls" element={<Protected><SearchCalls /></Protected>} />
            <Route path="/premise" element={<Protected><Premise /></Protected>} />
            <Route path="/crime" element={<Protected><CrimeIntel /></Protected>} />
            <Route path="/rms" element={<Protected><RMS /></Protected>} />
            <Route path="/rms-report/:id" element={<Protected><RmsReport /></Protected>} />
            <Route path="/rms-reports" element={<Protected><RmsReportsList /></Protected>} />
            <Route path="/daily-activity" element={<Protected><DailyActivity /></Protected>} />
            <Route path="/supervisor" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor"]}><Supervisor /></Protected>} />
            <Route path="/call-audit" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor", "police_dispatcher", "fire_dispatcher", "ems_dispatcher", "admin"]}><CallAudit /></Protected>} />
            <Route path="/transfer-log" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor", "admin"]}><TransferLog /></Protected>} />
            <Route path="/comms-daily" element={<Protected roles={["police_dispatcher", "fire_dispatcher", "ems_dispatcher", "call_taker", "supervisor", "watch_commander", "records_supervisor", "admin"]}><CommsDailyReport /></Protected>} />
            <Route path="/trainer" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor", "admin"]}><TrainerConsole /></Protected>} />
            <Route path="/mdt" element={<Protected><MDT /></Protected>} />
            <Route path="/comms" element={<Protected><Comms /></Protected>} />
            <Route path="/directory" element={<Protected><Directory /></Protected>} />
            <Route path="/service" element={<Protected roles={["field_unit", "pa_state_constable", "police_dispatcher", "records_supervisor", "supervisor", "watch_commander", "admin"]}><ServiceOfProcess /></Protected>} />            <Route path="/ncic" element={<Protected roles={["police_dispatcher", "fire_dispatcher", "ems_dispatcher", "call_taker", "field_unit", "pa_state_constable", "supervisor", "watch_commander", "records_supervisor"]}><NCIC /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
            <Route path="/help" element={<Protected><Help /></Protected>} />
            <Route path="/exclusions" element={<Protected><ExclusionNotices /></Protected>} />
            <Route path="/ia" element={<Protected roles={["supervisor", "watch_commander", "records_supervisor"]}><IA /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
